<template>
  <div v-if="isReady" class="company-procedures">
    <ui-heading tag="h3" no-top-margin>
      {{
        $t("identification_company_procedures", {
          companyName
        })
      }}
    </ui-heading>
    <div v-if="isNewIdent" class="new-procedure-marker">
      {{ $t("companyProcedures.new") }}
    </div>
    <div v-if="proceduresId">
      <div class="filter-container">
        <ui-heading tag="h6">
          {{ $t("companyProcedures.filterLabel") }}
        </ui-heading>
        <input
          list="countries"
          name="country"
          id="country"
          class="country-filter"
          :placeholder="$t('companyProcedures.filterPlaceholder')"
          @input="countryFilterValueChange"
          @focus="onFilterFocus"
          @blur="onFilterBlur"
        />
        <datalist id="countries">
          <option
            v-for="country in filteredCountryOptions"
            :key="country.key"
            :value="country.value"
          />
        </datalist>
      </div>
      <selfie-settings-section
        v-if="!isProhibited"
        :selfie-types="procedures[PROCEDURES_FIELDS.SELFIE_TYPES]"
        class="procedures-section"
      />
      <accepted-documents-section
        v-if="!isProhibited"
        :country-filter-code="countryFilterCode"
        :acceptable-documents="
          procedures[PROCEDURES_FIELDS.ACCEPTABLE_DOCUMENTS]
        "
        class="procedures-section"
      />
      <importants-section
        :importants="procedures[PROCEDURES_FIELDS.IMPORTANTS]"
        :country-filter-code="countryFilterCode"
        :is-prohibited="isProhibited"
        class="procedures-section"
      />
      <comment-section
        v-if="!isProhibited"
        :comment="procedures[PROCEDURES_FIELDS.COMMENT]"
        class="procedures-section"
      />
    </div>
    <div v-else>
      {{ $t("companyProcedures.emptyState") }}
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import { getters } from "@/store";
import { getCountryISOCode } from "@/utils/countries";
import {
  IMPORTANT_RULE,
  IMPORTANTS_FIELDS,
  PROCEDURES_FIELDS
} from "../../constants";
import countryCodes from "../../assets/isoCodes.json";
import AcceptedDocumentsSection from "./AcceptedDocumentsSection";
import SelfieSettingsSection from "./SelfieSettingsSection";
import ImportantsSection from "./ImportantsSection";
import CommentSection from "./CommentSection";
import { isCountryInTheList } from "./utils";

export default {
  components: {
    CommentSection,
    ImportantsSection,
    AcceptedDocumentsSection,
    SelfieSettingsSection
  },
  data() {
    return {
      PROCEDURES_FIELDS,
      countryFilterValue: null,
      countryFilterCode: null,
      countryCodes,
      isNewIdent: false,
      filterFocusElement: null,
      filteredCountryOptions: null,
      timerId: null,
      isReady: false
    };
  },

  computed: {
    ...mapGetters([getters.currentIdentification, getters.isWorking]),
    companyName() {
      return this.currentIdentification?.data?.companyName;
    },
    procedures() {
      return this.currentIdentification?.data?.procedures;
    },
    isProhibited() {
      const prohibitedCountriesItem = this.procedures[
        PROCEDURES_FIELDS.IMPORTANTS
      ].find(
        item =>
          item[IMPORTANTS_FIELDS.RULE] ===
          IMPORTANT_RULE.HAS_PROHIBITED_COUNTRIES
      );
      const prohibitedCountriesList = prohibitedCountriesItem
        ? prohibitedCountriesItem[IMPORTANTS_FIELDS.COUNTRIES_ISO_2]
        : [];

      return isCountryInTheList(
        this.countryFilterCode,
        prohibitedCountriesList
      );
    },
    countryOptions() {
      return (
        Object.keys(this.countryCodes).map(countryCode => {
          const countryCodeISO3 = getCountryISOCode(countryCode);
          const translatedCountry = this.$t(`countries.${countryCodeISO3}`);
          const countryValue = `${translatedCountry} (${countryCode})`;

          const countryItem = {
            key: countryCode,
            value: countryValue
          };

          return countryItem;
        }) ?? []
      ).sort((a, b) => a.value.localeCompare(b.value));
    },
    proceduresId() {
      return this.procedures?.id;
    }
  },

  watch: {
    companyName: function (val, oldVal) {
      this.isNewIdent = this.isWorking && oldVal !== val;
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.filteredCountryOptions = this.filterCountryOptions();
      this.isReady = true;
    });
  },
  beforeDestroy() {
    clearTimeout(this.timerId);
    this.timerId = null;
    this.filterFocusElement = null;
    document.removeEventListener("scroll", this.scrollHandler, true);
  },

  methods: {
    debounceFunc(func, delay) {
      clearTimeout(this.timerId);

      this.timerId = setTimeout(func, delay);
    },
    filterCountryOptions(countryFilterValue) {
      let startsWithList = [];
      let othersList = [];
      const filterValue = countryFilterValue?.toLowerCase();

      if (filterValue?.length < 1) {
        return [];
      }

      for (const country of this.countryOptions) {
        const countryValue = country.value?.toLowerCase();
        if (!countryValue) continue;

        if (countryValue.startsWith(filterValue)) {
          startsWithList.push(country);
        } else if (countryValue.includes(filterValue)) {
          othersList.push(country);
        }
      }

      return [...startsWithList, ...othersList];
    },
    countryFilterValueChange(event) {
      if (this.countryFilterValue === event.target.value) {
        return;
      }

      this.countryFilterValue = event.target.value;

      this.debounceFunc(
        () =>
          (this.filteredCountryOptions = this.filterCountryOptions(
            this.countryFilterValue
          )),
        200
      );

      this.countryFilterCode =
        this.countryOptions?.find(item => item.value === event.target.value)
          ?.key || null;
    },
    onFilterFocus(event) {
      this.filterFocusElement = event.target;
      document.addEventListener("scroll", this.scrollHandler, true);
    },
    onFilterBlur() {
      this.filterFocusElement = null;
      document.removeEventListener("scroll", this.scrollHandler, true);
    },
    scrollHandler() {
      this.filterFocusElement.blur();
    }
  }
};
</script>
<style>
.company-procedures select {
  height: 5rem;
  padding: 0.5rem;
  margin: 1rem 0 0;
  resize: vertical;
}

.company-procedures .country-list-item {
  font-weight: bold;
}

.company-procedures ::-webkit-scrollbar {
  width: 0.5rem;
}

.company-procedures ::-webkit-scrollbar-thumb {
  background-color: #a2a2a2;
  border-radius: 0.1rem;
  box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}
</style>
<style scoped>
.company-procedures {
  position: relative;
}

.country-filter {
  width: 25rem;
  padding: 0.5rem;
}

.procedures-section {
  margin-top: 2rem;
}

.filter-container {
  margin-top: 1rem;
  margin-bottom: 3rem;
}

.new-procedure-marker {
  position: absolute;
  top: 0.5rem;
  right: 2rem;
  color: #fe5a28;
}
</style>
